import React, { useEffect, useState } from 'react';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';
import { Calendar } from './ui/calendar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { CalendarOff, Trash2, Plus } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { getBlockedSlots, createBlockedSlot, deleteBlockedSlot } from '../services/api';
import { TIME_SLOTS, END_TIME_SLOTS, blockLabel, capitalizeFirst } from '../lib/timeSlots';

const TimeSelect = ({ value, onChange, options, testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger data-testid={testId}><SelectValue placeholder="--:--" /></SelectTrigger>
    <SelectContent>
      {options.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
    </SelectContent>
  </Select>
);

const BlockedSlotsTab = () => {
  const { toast } = useToast();
  const [blocks, setBlocks] = useState([]);
  const [date, setDate] = useState();
  const [allDay, setAllDay] = useState(true);
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const load = () => getBlockedSlots().then(setBlocks).catch(() =>
    toast({ title: 'Error', description: 'No se pudieron cargar los bloqueos', variant: 'destructive' }));

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSave = async () => {
    if (!date) return toast({ title: 'Falta la fecha', description: 'Selecciona una fecha', variant: 'destructive' });
    if (!allDay && (!startTime || !endTime || startTime >= endTime)) {
      return toast({ title: 'Horario inválido', description: 'La hora de inicio debe ser anterior a la de fin', variant: 'destructive' });
    }
    setSaving(true);
    try {
      await createBlockedSlot({
        date: format(date, 'yyyy-MM-dd'), all_day: allDay,
        start_time: allDay ? '' : startTime, end_time: allDay ? '' : endTime, reason: reason.trim(),
      });
      toast({ title: 'Bloqueo guardado', description: 'Los clientes ya no podrán reservar en ese horario' });
      setDate(undefined); setStartTime(''); setEndTime(''); setReason('');
      load();
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar este bloqueo? Los clientes podrán volver a reservar en ese horario.')) return;
    try {
      await deleteBlockedSlot(id);
      setBlocks((p) => p.filter((b) => b.id !== id));
      toast({ title: 'Bloqueo eliminado' });
    } catch (e) {
      toast({ title: 'Error', description: e.message, variant: 'destructive' });
    }
  };

  const today = new Date(); today.setHours(0, 0, 0, 0);

  return (
    <div className="grid lg:grid-cols-2 gap-6" data-testid="blocked-slots-tab">
      <Card>
        <CardHeader>
          <CardTitle>Bloquear fecha u horario</CardTitle>
          <CardDescription>Los clientes no podrán reservar en las fechas u horas bloqueadas.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div className="flex justify-center border rounded-md" data-testid="block-date-calendar">
            <Calendar key={blocks.length} mode="single" selected={date} onSelect={setDate} locale={es} disabled={(d) => d < today} />
          </div>
          <div className="flex items-center justify-between">
            <Label htmlFor="block-all-day">Bloquear todo el día</Label>
            <Switch id="block-all-day" checked={allDay} onCheckedChange={setAllDay} data-testid="block-all-day-switch" />
          </div>
          {!allDay && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Desde</Label>
                <TimeSelect value={startTime} onChange={setStartTime} options={TIME_SLOTS} testId="block-start-time" />
              </div>
              <div className="space-y-2">
                <Label>Hasta</Label>
                <TimeSelect value={endTime} onChange={setEndTime} options={END_TIME_SLOTS} testId="block-end-time" />
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="block-reason">Motivo (lo verán los clientes)</Label>
            <Textarea id="block-reason" value={reason} onChange={(e) => setReason(e.target.value)} maxLength={500}
              placeholder="Ej. Cerrado por evento privado" data-testid="block-reason-input" />
          </div>
          <Button onClick={handleSave} disabled={saving} className="w-full bg-black text-white hover:bg-gray-800" data-testid="block-save-btn">
            <Plus className="w-4 h-4 mr-2" />{saving ? 'Guardando...' : 'Guardar bloqueo'}
          </Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Bloqueos activos</CardTitle>
          <CardDescription>Fechas de hoy en adelante.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-3" data-testid="blocked-slots-list">
          {blocks.length === 0 && (
            <div className="text-center text-gray-500 py-10" data-testid="blocked-slots-empty">
              <CalendarOff className="w-10 h-10 mx-auto mb-2 text-gray-300" />
              No hay fechas bloqueadas
            </div>
          )}
          {blocks.map((b) => (
            <div key={b.id} className="flex items-start justify-between gap-3 border rounded-lg p-3" data-testid={`blocked-slot-${b.id}`}>
              <div>
                <p className="font-semibold">{capitalizeFirst(format(parseISO(b.date), "EEEE d 'de' MMMM yyyy", { locale: es }))}</p>
                <p className="text-sm text-gray-600">{blockLabel(b)}</p>
                {b.reason && <p className="text-sm text-gray-500 italic mt-1">“{b.reason}”</p>}
              </div>
              <Button variant="ghost" size="icon" onClick={() => handleDelete(b.id)} data-testid={`delete-blocked-slot-${b.id}`}>
                <Trash2 className="w-4 h-4 text-red-500" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default BlockedSlotsTab;
